import { StrictMode, useCallback, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { ClerkProvider } from '@clerk/react';
import { App } from './App.jsx';
import { createConsoleApi } from './console-api.js';
import type { ConsoleServices } from './console-services.jsx';
import { createMetaSignupClient } from './meta-signup.js';
import { createHttpOnboardingStore } from './onboarding-storage.js';
import { loadConsoleConfig, type ConsoleConfig } from './config.js';
import './styles.css';

/**
 * Composition root. Everything the app depends on is constructed here and injected — no module
 * builds its own client, and nothing reaches for a global. See ADR 0001.
 */
function ConsoleRoot() {
  const [config, setConfig] = useState<ConsoleConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    loadConsoleConfig()
      .then((loaded) => {
        if (!isMounted) {
          return;
        }

        setConfig(loaded);
      })
      .catch((loadError: unknown) => {
        if (!isMounted) {
          return;
        }

        setError(messageOf(loadError));
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const createServices = useCallback((getToken: () => Promise<string | null>): ConsoleServices => {
    if (!config) {
      throw new Error('Configuração da área da loja ainda não carregada.');
    }

    return {
      api: createConsoleApi({ backendOrigin: config.backendOrigin, getToken }),
      signup: createMetaSignupClient({
        appId: config.metaAppId,
        configId: config.metaConfigId,
      }),
      googleMapsApiKey: config.googleMapsApiKey,
    };
  }, [config]);

  const createStore = useCallback((services: ConsoleServices, userId: string) => {
    if (!services.api) {
      throw new Error('Não foi possível preparar sua área da loja.');
    }

    return createHttpOnboardingStore({ api: services.api, userId });
  }, []);

  if (error) {
    return <BootCard title="Não foi possível abrir sua área da loja" tone="error" message={error} />;
  }

  if (!config) {
    return <BootCard title="Carregando" message="Preparando sua área da loja…" />;
  }

  return (
    <ClerkProvider publishableKey={config.clerkPublishableKey}>
      <App createServices={createServices} createStore={createStore} />
    </ClerkProvider>
  );
}

function BootCard({
  title,
  message,
  tone,
}: {
  title: string;
  message: string;
  tone?: 'error';
}) {
  return (
    <main className="flex min-h-dvh items-center justify-center bg-base-200 p-6">
      <div className="card w-full max-w-md border border-base-300 bg-base-100 shadow-sm">
        <div className="card-body">
          <h1 className="card-title">{title}</h1>
          <p role={bootRole(tone)} className="opacity-70">
            {message}
          </p>
        </div>
      </div>
    </main>
  );
}

function bootRole(tone?: 'error'): 'alert' | 'status' {
  if (tone === 'error') {
    return 'alert';
  }

  return 'status';
}

function messageOf(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return 'Não foi possível abrir sua área da loja agora. Tente novamente em instantes.';
}

const container = document.getElementById('app');

if (!container) {
  throw new Error('Elemento #app não encontrado no documento.');
}

createRoot(container).render(
  <StrictMode>
    <ConsoleRoot />
  </StrictMode>,
);
